/**
 * "Every open item" rows (R13). Order and the Act by, State, Who acts and Balance cells
 * mirror `tracking/services/action_plan_export.py`; both tests assert
 * `fixtures/action_plan/related_2026-10-08_rows.json`, so a one-sided change fails.
 */
import {
  FLOW_KINDS,
  JUDGMENT_STEP_DOB_CERT,
  JUDGMENT_STEP_DOB_IF_ACCEPTED,
  JUDGMENT_STEP_FDNY_CERT,
  JUDGMENT_STEP_FDNY_IF_ACCEPTED,
  JUDGMENT_STEP_PAY,
  KIND_BOILER_INSPECT,
  KIND_BOILER_REINSPECT,
  KIND_BOILER_REPORT_MISSED,
  KIND_DEP_REGISTRATION,
  KIND_ELEVATOR_BRAKE,
  KIND_GAS_ALARMS,
  KIND_HPD_BILL,
  LANE_HPD,
  LANE_LL33,
  MONEY_ESTIMATE,
  handLive,
  type ActionItem,
  type ActionPlanFeed,
  type BuildingGroup,
  type JudgmentStep,
  type RailState,
} from "@/components/portal/useActionPlanFeed";
import { JUDGMENT_OWED, formatUsdCents } from "@/components/portal/casesDocket";
import { formatUsdExact } from "@/components/portal/exposureAxes";
import { PAPERS_OWED } from "@/components/portal/hpdViolations";
import { buildingStreetLine } from "@/components/portal/portalBuildingsTable";
import { allItems } from "./actionPlan";
import { LAW_KIND, LAWS_LABEL } from "./laws/lawTable";
import { sinceOf, withYear } from "./rail/judgmentRail";

export type RowGroup = "dated" | "owed" | "past" | "none";

export const GROUP_LABEL: Record<Exclude<RowGroup, "dated">, string> = {
  owed: JUDGMENT_OWED,
  past: "Past date, still fixable",
  none: "No date yet",
};

export const STATE_LABEL: Record<RailState, string> = {
  past: "Past date, fixable",
  due: "Date ahead",
  x: "Penalty final",
  open: "Paid, fix not proven",
  sent: "Certified, HPD verifying",
};

/** Header legend: the shorter R13 names. */
export const RAIL_LABEL: Record<RailState, string> = {
  past: "Past date, still fixable",
  x: "Penalty final",
  due: "Date ahead",
  open: "Paid, fix not proven",
  sent: "HPD verifying",
};

const WHO: Record<string, string> = {
  you: "You",
  codeclear: "CodeClear; you sign",
  agency: "HPD",
};

/** Export `OATH_KINDS`: "You", never the vendor (export `who_cell`). */
export const OATH_KINDS = [
  "reopen",
  "reason",
  "check_result",
  "hearing",
  "new_hearing",
  "appeal",
];

/** Its Who acts is the served whoActs, never WHO_BY_KIND. */
export const LL33_KIND = "ll33_post";

export const PAY_KIND = "pay";
export const PROOF_KIND = "proof";
export const NEW_HEARING_KIND = "new_hearing";
export const BOILER_KIND = "boiler_report";
export const DOB_CERTIFY_KIND = "dob_certify";
/** action_items.FDNY_CERTIFY_KIND: a defaulted FDNY ticket whose certificate staff marked due (Nov 2). */
export const FDNY_CERTIFY_KIND = "fdny_certify";
/** action_items.DOB_NOW_KINDS: DOB NOW penalties, paid at DOB, never on an OATH record. */
export const DOB_NOW_KINDS: readonly string[] = [DOB_CERTIFY_KIND, BOILER_KIND];

/** Export `WHO_BY_KIND`. */
const WHO_BY_KIND: Record<string, string> = {
  [PAY_KIND]: "Respondent officer",
  [PROOF_KIND]: "Vendor",
  [BOILER_KIND]: "Boiler vendor",
  [DOB_CERTIFY_KIND]: "Signer",
  [FDNY_CERTIFY_KIND]: "Signer",
  hpd_due: "Super",
  hpd_past: "Super, vendors",
  hpd_records: "Owner or agent",
  hpd_papers: "Owner or agent",
  [KIND_BOILER_INSPECT]: "Inspector",
  [KIND_BOILER_REINSPECT]: "Inspector",
  [KIND_BOILER_REPORT_MISSED]: "Account holder",
  [KIND_DEP_REGISTRATION]: "You",
  [KIND_ELEVATOR_BRAKE]: "Elevator agency",
  [KIND_GAS_ALARMS]: "You",
};

/** Export `MISSED_STATE`: a missed past-cycle report has no date left; DOB bills it. */
export const MISSED_STATE = "Report missed";

/** Export `HPD_PAPERS_STATE`: HPD papers ride rail state "open", whose label is about paid tickets. */
export const HPD_PAPERS_STATE = `HPD saw it fixed, ${PAPERS_OWED.toLowerCase()}`;

/** A judgment unit on a flag-on feed (the codec drops the key off, so OFF = HEAD). */
export const isJudgment = (feed: ActionPlanFeed, item: ActionItem): boolean =>
  !!feed.judgmentOwed && item.judgmentOwed === true;
/** The rail state a row counts under: action_plan_lanes.annotate counts every judgment under x, whatever its state. */
export const railState = (feed: ActionPlanFeed, item: ActionItem) =>
  isJudgment(feed, item) ? "x" : item.state;

/** An undated judgment (from Mon Jan 11, or unpriced): its state x reads Judgment owed. Absent with the flag off. */
export const owedX = (item: ActionItem) =>
  !!item.judgmentOwed && item.state === "x";

/** Header legend: open less its HPD papers, then the papers in their own words; penalty final less its judgments
 * owed, then those (C7: the canvas's name); empty entries drop. The server counts every judgment under x whatever
 * its row's state (2A action_plan_lanes.annotate), so the split takes them all, never more than x holds. */
export const railLegend = (
  rail: ActionPlanFeed["rail"],
  items: readonly ActionItem[] = [],
) => {
  const owed = laneCount(items.filter((i) => i.judgmentOwed === true));
  return rail
    .flatMap((r) => {
      const part = r.state === "x" ? Math.min(owed, r.count) : (r.papers ?? 0);
      return [
        {
          key: r.state,
          state: r.state,
          label: RAIL_LABEL[r.state],
          count: r.count - part,
        },
        {
          key: `${r.state}-${r.state === "x" ? "owed" : "papers"}`,
          state: r.state,
          label: r.state === "x" ? JUDGMENT_OWED : HPD_PAPERS_STATE,
          count: part,
        },
      ];
    })
    .filter((e) => e.count > 0);
};

/** Export `state_cell`: a default with a date left, an unknown result, an undated due. */
export const stateCell = (item: ActionItem): string => {
  if (!item.state) return "";
  if (item.kind === "check_result") return "Result not on file yet";
  if (item.kind === "hpd_papers") return HPD_PAPERS_STATE;
  if (item.kind === KIND_BOILER_REPORT_MISSED) return MISSED_STATE;
  if (item.state === "past" && ["reopen", "reason"].includes(item.kind))
    return "Defaulted, fixable";
  if (item.state === "due" && item.nodeAt === "none")
    return "Open, no date set";
  if (owedX(item)) return JUDGMENT_OWED;
  return STATE_LABEL[item.state];
};

/** An item a count reads: not an LL33 door, not an undated due. */
const counted = (item: ActionItem): boolean =>
  !!item.state &&
  laneOf(item) !== "LL33" &&
  !(item.state === "due" && item.nodeAt === "none");

/** Rail rule (`action_plan_lanes.annotate`): counted, less HPD repair bills. */
export const onRail = (item: ActionItem): boolean =>
  counted(item) && item.kind !== KIND_HPD_BILL;

const sum = (items: readonly ActionItem[]) =>
  items.reduce((s, i) => s + i.groupedCount, 0);

/** Violations a lane counts, as the deck's lane header and the rail's lane code read it. */
export const laneCount = (items: readonly ActionItem[]): number =>
  sum(items.filter(onRail));

/** The Ontology's count (dock chip, Later): laneCount with HPD repair bills kept, as a stage's n keeps them. */
export const itemCount = (items: readonly ActionItem[]): number =>
  sum(items.filter(counted));

/** Advice date first, else actBy (LL33: Fri Oct 30), else a judgment row's amnesty date (ruling 3); a past item
 * already behind today has none. */
export const shownAct = (item: ActionItem, today: string): string | null => {
  const act = item.adviceBy ?? item.actBy ?? item.sortBy ?? null;
  return item.state === "past" && act && act < today ? null : act;
};

/** HEAD's two checks first, so only an undated judgment reaches "owed" (ruling 7). */
export const groupOf = (item: ActionItem, today: string): RowGroup => {
  if (shownAct(item, today)) return "dated";
  if (item.state === "past") return "past";
  return item.judgmentOwed && item.judgmentStep == null ? "owed" : "none";
};

/** One order everywhere (Leo 10-31 B5, design ruling B): the cases docket and the case page rank the same way. */
const GROUP_RANK: Record<RowGroup, number> = {
  dated: 0,
  owed: 1,
  past: 2,
  none: 3,
};

export const orderRows = <T extends ActionItem>(
  items: readonly T[],
  today: string,
): T[] =>
  [...items].sort((a, b) => {
    const ga = GROUP_RANK[groupOf(a, today)];
    const gb = GROUP_RANK[groupOf(b, today)];
    const keys: [string, string][] = [
      [shownAct(a, today) ?? "", shownAct(b, today) ?? ""],
      [a.state === "past" ? "0" : "1", b.state === "past" ? "0" : "1"],
      [a.address, b.address],
      [a.key, b.key],
    ];
    if (ga !== gb) return ga - gb;
    const diff = keys.find(([x, y]) => x !== y);
    if (!diff) return 0;
    return diff[0] < diff[1] ? -1 : 1;
  });

export const laneOf = (item: ActionItem): string => item.lane ?? item.agency;

/** Export `agency_cell`: the lane, except OATH's, whose tickets name their issuer (DSNY, DOHMH). */
export const agencyCell = (item: ActionItem): string =>
  laneOf(item) === "OATH" ? item.agency : laneOf(item);

/** Writer: services/action_items.oath_item_key. Email links carry it. */
export const OATH_KEY_PREFIX = "oath:";

/** The one `?it=` fallback (Page, FocusRail): the exact key, else the smallest key holding oath:<ticket>, since the
 * key flips with the date tier (CTO M3) and lot-mates share a ticket. */
export const itemForLink = (
  items: readonly ActionItem[],
  key: string,
): ActionItem | null => {
  const exact = items.find((i) => i.key === key);
  if (exact) return exact;
  if (!key.startsWith(OATH_KEY_PREFIX)) return null;
  const ticket = key.slice(OATH_KEY_PREFIX.length);
  return items
    .filter((i) => i.recordIds.includes(ticket))
    .reduce<ActionItem | null>((a, i) => (a && a.key < i.key ? a : i), null);
};

/** The one Get help path: no case yet and the feed says eligible (detail and rail). */
export const canGetHelp = (item: ActionItem): boolean =>
  !item.case && item.getHelpEligible;

/** "014214487K", "6 items", or the server's LL33 door words (export row()[2]). */
export const violationCell = (item: ActionItem): string => {
  if (item.groupedCount === 1 && item.recordIds.length)
    return item.recordIds[0];
  if (item.recordLabel) return item.recordLabel;
  if (laneOf(item) === "LL33") return "";
  // A law row is no record and no item (laws plan G12): its filing names it.
  if (item.kind === LAW_KIND) return item.action;
  const noun = (FLOW_KINDS.has(item.kind) && item.recordNoun) || "item";
  return `${item.groupedCount} ${noun}${item.groupedCount === 1 ? "" : "s"}`;
};

/** The item's name in the rail and tooltip: LL33 reads as door labels, never its door count (R13). */
export const nameOf = (item: ActionItem): string =>
  laneOf(item) === "LL33" ? "Door labels" : violationCell(item);

export const whatCell = (item: ActionItem): string =>
  item.subject ? `${item.action}: ${item.subject}` : item.action;

/** A handed hearing: CodeClear has it, the owner still appears at OATH (export who_cell). */
export const youAppear = (item: ActionItem): boolean =>
  item.owner === "you" && handLive(item.handoff);

export const whoCell = (item: ActionItem): string => {
  if (!item.owner) return "";
  if (youAppear(item)) return "CodeClear; you appear";
  if (item.owner !== "you") return WHO[item.owner];
  if (OATH_KINDS.includes(item.kind)) return "You";
  if (item.kind === LL33_KIND || item.whoActs != null) {
    if (item.whoActs == null) throw new Error("ll33_post without who_acts");
    return item.whoActs;
  }
  const who = WHO_BY_KIND[item.kind] ?? "You";
  return item.needs.includes("VENDOR") && !who.includes("endor")
    ? `${who}, vendor`
    : who;
};

/** Export `row()[0]`: the date, or "Past date" / "No date yet"; an undated judgment "Judgment since Mon Jul 31,
 * 2023" from the rail's reader (B6). With no docket date it keeps HEAD's cell, never saying Judgment owed twice. */
export const actCell = (item: ActionItem, today: string): string => {
  const since = groupOf(item, today) === "owed" && sinceOf(item, today);
  if (since) return `Judgment since ${withYear(since)}`;
  return (
    shownAct(item, today) ??
    (item.state === "past" ? "Past date" : "No date yet")
  );
};

/** A judgment row's line under its date (ruling 9); export `STEP_WORD`. One wording per flow: notice_plan's
 * `STEP_LABEL["amnesty"]` is the pay word. */
const PAY_WORD = "Amnesty price";
export const STEP_WORD: Record<JudgmentStep, string> = {
  [JUDGMENT_STEP_PAY]: PAY_WORD,
  [JUDGMENT_STEP_FDNY_CERT]: "FDNY certificate first",
  [JUDGMENT_STEP_DOB_CERT]: "DOB certificate first",
  [JUDGMENT_STEP_FDNY_IF_ACCEPTED]: PAY_WORD, // Jan 9-10: the pay word
  [JUDGMENT_STEP_DOB_IF_ACCEPTED]: PAY_WORD,
};
export const INTEREST_RUNNING = "Interest running";

/** The Act by cell's second line: the step's word, or Interest running on an undated judgment; "" otherwise. */
export const actLine2 = (item: ActionItem, today: string): string => {
  if (item.judgmentStep) return STEP_WORD[item.judgmentStep];
  return groupOf(item, today) === "owed" ? INTEREST_RUNNING : "";
};

export const NOT_ON_FILE = "Not on file";
/** Export `PAID`: a proof row's tickets are paid, certification due; the rows golden pins both. */
export const PAID = "Paid";

/** HPD, LL33, a law row, boiler and DEP (export `_balance`) carry no money column. */
const noMoney = (item: ActionItem): boolean =>
  item.agency === LANE_HPD ||
  laneOf(item) === LANE_LL33 ||
  item.kind === LAW_KIND ||
  FLOW_KINDS.has(item.kind) ||
  item.money.kind === MONEY_ESTIMATE;

/** Blank for noMoney rows and a hearing's charge; "Not on file", never $0. `note`: the server's
 * word on an amount the city data lacks (the boiler's $1,000: boiler_tickets.estimate_note); every surface prints it. */
export const balanceCell = (
  item: ActionItem,
): { text: string; money: boolean; note?: string } => {
  const { kind, usd, note } = item.money;
  if (noMoney(item) || kind === "charged") return { text: "", money: false };
  if (item.kind === PROOF_KIND) return { text: PAID, money: false };
  if (!usd) return { text: NOT_ON_FILE, money: false };
  if (item.kind === NEW_HEARING_KIND)
    return { text: `On record ${formatUsdExact(usd)}`, money: false };
  return { text: formatUsdCents(usd), money: true, ...(note ? { note } : {}) };
};

/** The amounts balanceCell leaves out: a hearing's charge, a new hearing's amount on record (Pipeline sizes). */
export const hearingMoney = (item: ActionItem): boolean =>
  !noMoney(item) &&
  !!item.money.usd &&
  (item.money.kind === "charged" || item.kind === NEW_HEARING_KIND);

/** "14-50 GATEWAY BOULEVARD, FAR ROCKAWAY" -> "14-50 Gateway Boulevard". */
export const streetName = (address: string): string =>
  buildingStreetLine(address)
    .toLowerCase()
    .replace(
      /(^|\s)([a-z])/g,
      (_, s: string, c: string) => s + c.toUpperCase(),
    );

/** R14 short street: "20-31 19th St", "14-50 Gateway Blvd". */
export const shortStreet = (address: string) =>
  streetName(address)
    .replace(/ Boulevard\b/, " Blvd")
    .replace(/ Street\b/, " St")
    .replace(/ Avenue\b/, " Ave")
    .replace(/ Ave\./, " Ave");

export interface BuildingOption {
  id: string;
  name: string;
  count: number;
  group: BuildingGroup | null;
}

/** Filter options: buildings with open violations, most first, counted by the rail rule
 * so a building's count is its header total; `of` counts every building. */
export const buildingOptions = (
  feed: ActionPlanFeed,
): { options: BuildingOption[]; of: number } => {
  const counts = new Map<string, number>();
  allItems(feed)
    .filter(onRail)
    .forEach((i) =>
      counts.set(
        i.buildingId,
        (counts.get(i.buildingId) ?? 0) + i.groupedCount,
      ),
    );
  const options = [...counts]
    .map(([id, count]) => ({
      id,
      count,
      group: feed.buildings[id]?.group ?? null,
      name: streetName(
        feed.buildings[id]?.address ||
          allItems(feed).find((i) => i.buildingId === id)?.address ||
          "",
      ),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return {
    options,
    of: Math.max(options.length, Object.keys(feed.buildings).length),
  };
};

/** An unread building's count slot (plan_final §8): never 0. */
export const NOT_READ = "Not read";

/** Building ids read as nothing, never as 0 (plan_final §8): HPD unread, and not fetched; `ids` narrows. */
export interface Unread {
  hpd: string[];
  notFetched: string[];
}
export const unreadFor = (
  feed: ActionPlanFeed,
  ids?: readonly string[],
): Unread => {
  const keep = (id: string) => !ids || ids.includes(id);
  return {
    hpd: (feed.lanes.find((l) => l.key === LANE_HPD)?.unread ?? []).filter(
      keep,
    ),
    notFetched: feed.fetchState.buildingsNotFetched
      .map((b) => b.id)
      .filter(keep),
  };
};

/** Every unread building once: the tile, the goal's name, the stack and the list's empty line. */
export const unreadIds = (u: Unread): string[] => [
  ...new Set([...u.hpd, ...u.notFetched]),
];

const addressList = (feed: ActionPlanFeed, ids: readonly string[]) =>
  ids.map((id) => feed.buildings[id]?.address || id).join("; ");

/** Export `gap_notes`, HPD line: "HPD not read yet: <addresses>"; `ids` narrows. */
export const hpdUnreadNote = (
  feed: ActionPlanFeed,
  ids?: readonly string[],
): string | null => {
  const { hpd } = unreadFor(feed, ids);
  return hpd.length ? `HPD not read yet: ${addressList(feed, hpd)}` : null;
};

/** The page's unread notes (FetchNotes) and the onto rail's; `ids` narrows; `lawUnread`: failed /codes/ reads. Missing, never 0. */
export const unreadLines = (
  feed: ActionPlanFeed,
  ids?: readonly string[],
  lawUnread: readonly string[] = [],
): string[] => {
  const hpd = hpdUnreadNote(feed, ids);
  const lines: string[] = hpd
    ? [`${hpd}. Those violations are missing, not zero.`]
    : [];
  const off = unreadFor(feed, ids).notFetched;
  const notFetched = feed.fetchState.buildingsNotFetched.filter((b) =>
    off.includes(b.id),
  );
  if (notFetched.length) {
    lines.push(
      `Ticket data was not read for ${notFetched
        .map((b) => buildingStreetLine(b.address))
        .join(", ")}. Items there are missing, not zero.`,
    );
  }
  const laws = lawUnread.filter((id) => !ids || ids.includes(id));
  if (laws.length) {
    lines.push(
      `${LAWS_LABEL} not read yet: ${addressList(feed, laws)}. Those filings are missing, not zero.`,
    );
  }
  return lines;
};

/** `scope`: one id or a comma list; `agencies`: deck lanes, as the page's `?a=`. */
export const exportUrl = (scope?: string, agencies?: string): string => {
  const q = [
    ["building", scope],
    ["agency", agencies],
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join("&");
  return `/api/tracking/portal/action-plan/export/${q ? `?${q}` : ""}`;
};
